import { useCallback, useEffect, useState } from 'react';

import { useRecentReports } from './useRecentReports';

export interface UseReportsModalReturn {
  /** Recently opened report codes for this page — feed to `ReportTabs`. */
  recentCodes: string[];
  isReportsModalOpen: boolean;
  openReportsModal: () => void;
  closeReportsModal: () => void;
}

/**
 * Wires a list page to its saved reports: the Reports-button modal's open state
 * and the recency list behind the tab strip's `+` menu.
 *
 * Bundled into one hook because all three pieces move together — a page either
 * has a Reports button and therefore a recents-based `+`, or it has neither
 * (see `ReportPickerMenu`, which keeps the full catalogue when `recentCodes` is
 * omitted). Keeping them together stops a page from getting half of it.
 *
 * @param pageName report page key, e.g. 'ServiceList'.
 * @param activeReport the page's currently applied report code, or null.
 */
export const useReportsModal = (
  pageName: string,
  activeReport: string | null
): UseReportsModalReturn => {
  const [isReportsModalOpen, setReportsModalOpen] = useState(false);
  const { recentCodes, recordRecent } = useRecentReports(pageName);

  // One place records recency for every route into a report — a tab, a
  // ?savedReportCode deep link, or the Reports modal — because they all land on
  // activeReport.
  useEffect(() => {
    if (activeReport) recordRecent(activeReport);
  }, [activeReport, recordRecent]);

  const openReportsModal = useCallback(() => setReportsModalOpen(true), []);
  const closeReportsModal = useCallback(() => setReportsModalOpen(false), []);

  return { recentCodes, isReportsModalOpen, openReportsModal, closeReportsModal };
};
